/**
 * Signature-help tooltip: while the caret sits inside a function call, shows that function's
 * syntax with the argument currently being typed in bold.
 *
 * Built on `tokenizeLenient` (from `formulaLanguage.ts`) rather than a second bespoke character
 * scanner over the raw string — the tokenizer already resolved string/sheet-literal boundaries and
 * bracket-escaped column names into single atomic tokens, so finding the enclosing call is just a
 * backward walk over parens and the locale's argument separator, with no need to re-derive quote
 * escaping. `tokenizeLenient` never throws, so this works on the same in-progress, often-invalid
 * text the highlighter sees.
 */

import { StateEffect, StateField, type Extension } from '@codemirror/state'
import { EditorView, ViewPlugin, showPanel, showTooltip, type Panel, type Tooltip } from '@codemirror/view'
import type { EditorState } from '@codemirror/state'
import { currentCompletions } from '@codemirror/autocomplete'
import type { FormulaLocale, FunctionMetadata } from '@/shared/formula/language'
import { tokenizeLenient, type LenientToken } from './formulaLanguage'

export interface SignatureInfo {
  funcName: string
  syntax: string
  description: string
  /** 0-based index of the argument the caret currently sits in. */
  activeArgIndex: number
  /** Position of the end of the enclosing `(`, where the tooltip anchors. */
  anchorPos: number
}

/**
 * Scans backward from `pos` over the token stream, tracking paren nesting, to find the `(` of the
 * nearest call that encloses the caret and isn't already closed. Counts the locale's argument
 * separator at that same nesting level to know which argument the caret is in. Returns `null` when
 * the caret isn't inside a recognized call (including a call whose name isn't in `functionMetadata`
 * — a formula stored before a function was removed, or plain `(grouping)`).
 *
 * `functionMetadata` supplies the locale-aware `syntax`/`description` (see
 * `formulaFunctionMetadata.ts`) — the tokenizer itself already only ever tags a token `func` when
 * its name is a real entry in `FUNCTIONS`, so this lookup is expected to always succeed for a
 * `func` token; it stays defensive (`null` on a miss) rather than asserting.
 */
export function findEnclosingCall(
  text: string,
  pos: number,
  locale: FormulaLocale,
  functionMetadata: readonly FunctionMetadata[],
): SignatureInfo | null {
  const tokens: LenientToken[] = tokenizeLenient(text, locale)
  const before = tokens.filter((tok) => tok.start < pos)

  let depth = 0
  let activeArgIndex = 0
  let openIndex = -1
  for (let i = before.length - 1; i >= 0; i--) {
    const tok = before[i]!
    if (tok.kind === 'close') {
      depth++
    } else if (tok.kind === 'open') {
      if (depth === 0) {
        openIndex = i
        break
      }
      depth--
    } else if (tok.kind === 'argSep' && depth === 0) {
      activeArgIndex++
    }
  }
  if (openIndex === -1) return null

  const funcTok = before[openIndex - 1]
  if (!funcTok || funcTok.kind !== 'func' || funcTok.name === undefined) return null

  const meta = functionMetadata.find((fn) => fn.name === funcTok.name)
  if (!meta) return null

  return {
    funcName: funcTok.name,
    syntax: meta.syntax,
    description: meta.description,
    activeArgIndex,
    anchorPos: before[openIndex]!.end,
  }
}

/**
 * `"NAME(arg1; arg2; ...)"` → `["arg1", "arg2", "..."]`. Every `FunctionSpec.syntax` in
 * `language.ts` is authored with a literal `;`, independent of the runtime formula locale (Spanish
 * argument names, English `;`) — so this always splits on `;`, never `locale.argument`.
 */
function parseArgLabels(syntax: string): string[] {
  const open = syntax.indexOf('(')
  const close = syntax.lastIndexOf(')')
  if (open === -1 || close === -1 || close <= open) return []
  const inner = syntax.slice(open + 1, close).trim()
  return inner === '' ? [] : inner.split(';').map((s) => s.trim())
}

export interface SignatureHelpLabels {
  /** Title of the × that hides the help. */
  hide: string
  /** Title of the ? that shows it again. */
  show: string
}

const HIDDEN_KEY = 'numtable_formula_help_hidden'

function readHidden(): boolean {
  try {
    return localStorage.getItem(HIDDEN_KEY) === '1'
  } catch {
    return false
  }
}

// Like Google Sheets, closing the help hides it in every formula editor until the user asks for it
// again, so the choice lives outside any one EditorState and every live editor is told about it.
let helpHidden = readHidden()
const liveViews = new Set<EditorView>()
const helpToggled = StateEffect.define<null>()

function setHelpHidden(hidden: boolean) {
  helpHidden = hidden
  try {
    if (hidden) localStorage.setItem(HIDDEN_KEY, '1')
    else localStorage.removeItem(HIDDEN_KEY)
  } catch { /* per-browser nicety only */ }
  for (const view of liveViews) view.dispatch({ effects: helpToggled.of(null) })
}

const trackLiveViews = ViewPlugin.define((view) => {
  liveViews.add(view)
  return { destroy: () => liveViews.delete(view) }
})

function iconButton(className: string, text: string, title: string, hidden: boolean): HTMLButtonElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = className
  button.textContent = text
  button.title = title
  button.setAttribute('aria-label', title)
  // Keep the caret in the editor: a mousedown that moves focus would end the edit.
  button.addEventListener('mousedown', (event) => event.preventDefault())
  button.addEventListener('click', () => setHelpHidden(hidden))
  return button
}

function renderSignature(info: SignatureInfo, labels: SignatureHelpLabels): HTMLElement {
  const dom = document.createElement('div')
  dom.className = 'cm-formula-signature'
  dom.appendChild(iconButton('cm-formula-signature-close', '×', labels.hide, true))

  const args = parseArgLabels(info.syntax)
  // A variadic function's last shown argument (e.g. SUM's "...") stands for every further one.
  const activeIndex = args.length === 0 ? -1 : Math.min(info.activeArgIndex, args.length - 1)

  const head = document.createElement('div')
  const name = document.createElement('span')
  name.className = 'cm-formula-signature-name'
  name.textContent = `${info.funcName}(`
  head.appendChild(name)

  args.forEach((arg, i) => {
    if (i > 0) head.appendChild(document.createTextNode('; '))
    const argEl = document.createElement(i === activeIndex ? 'b' : 'span')
    argEl.textContent = arg
    head.appendChild(argEl)
  })
  head.appendChild(document.createTextNode(')'))
  dom.appendChild(head)

  if (info.description !== '') {
    const desc = document.createElement('div')
    desc.className = 'cm-formula-signature-desc'
    desc.textContent = info.description
    dom.appendChild(desc)
  }

  return dom
}

function renderShowButton(labels: SignatureHelpLabels): HTMLElement {
  return iconButton('cm-formula-signature-show', '?', labels.show, false)
}

function computeInfo(
  state: EditorState,
  locale: FormulaLocale,
  functionMetadata: readonly FunctionMetadata[],
): SignatureInfo | null {
  // The completion popup (and its own per-item info panel) anchors near the same position and
  // isn't coordinated with this tooltip's placement, so the two would otherwise overlap — e.g.
  // right after typing a call's `(`, which opens a nested-call completion list AND sits inside
  // the enclosing call. `currentCompletions` (unlike `completionStatus`) reflects whether the
  // dialog is actually rendered on screen, not just whether a source recently produced a result,
  // so signature help correctly resumes as soon as the popup visually closes. Autocomplete is the
  // interactive surface; step aside while it's genuinely showing.
  if (currentCompletions(state).length > 0) return null
  return findEnclosingCall(state.doc.toString(), state.selection.main.head, locale, functionMetadata)
}

function renderHelp(info: SignatureInfo, labels: SignatureHelpLabels): HTMLElement {
  return helpHidden ? renderShowButton(labels) : renderSignature(info, labels)
}

/**
 * `docked` (the sidebar's multi-line editor) shows the help as a panel under the text instead of a
 * tooltip at the call's `(`, which there would cover the very lines being written.
 */
export function formulaSignatureHelp(
  locale: FormulaLocale,
  functionMetadata: readonly FunctionMetadata[],
  labels: SignatureHelpLabels,
  docked = false,
): Extension {
  const field = StateField.define<SignatureInfo | null>({
    create: (state) => computeInfo(state, locale, functionMetadata),
    // Recomputed on every transaction, not just docChanged/selection: the completion popup can
    // open or close via an effect-only transaction (e.g. its options resolving a tick after the
    // keystroke that requested them), and this tooltip's visibility depends on that. Formulas are
    // short, so re-tokenizing on each transaction is cheap. A `helpToggled` effect lands here too.
    update(_info, tr) {
      return computeInfo(tr.state, locale, functionMetadata)
    },
    provide: (f) =>
      docked
        ? showPanel.from(f, (info) => (info ? (): Panel => ({ dom: renderHelp(info, labels), top: false }) : null))
        : showTooltip.from(f, (info): Tooltip | null =>
            info
              ? {
                  pos: info.anchorPos,
                  above: false,
                  create: (view) => ({
                    dom: renderHelp(info, labels),
                    // Keep the `(`'s x, but drop below the formula's last (wrapped) line so the
                    // help never covers text still being written.
                    getCoords: (pos) => {
                      const at = view.coordsAtPos(pos) ?? { left: 0, right: 0, top: 0, bottom: 0 }
                      const end = view.coordsAtPos(view.state.doc.length, -1)
                      const bottom = Math.max(at.bottom, end?.bottom ?? at.bottom)
                      return { left: at.left, right: at.right, top: at.top, bottom }
                    },
                  }),
                }
              : null,
          ),
  })
  return [field, trackLiveViews]
}
